import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CrntButton, CrntCallout } from '@crnt/web-angular';
import { HeaderComponent } from '../../elements/header/header.component';
import { FooterComponent } from '../../elements/footer/footer.component';
import { EmailPreferenceCardComponent } from '../../elements/email-preference-card/email-preference-card.component';
import { MandatoryEmailInfoCardComponent } from '../../elements/mandatory-email-info-card/mandatory-email-info-card.component';
import { PreferenceFormService } from '../../services/preference-form.service';
import { OptOutService } from '../../services/optout.service';
import { ThemeService } from '../../services/theme.service';
import { TranslationService } from '../../services/translation.service';
import { TokenService } from '../../services/token.service';
import type { OptoutJwtTokenPayload } from '../../models/jwtToken.interface';
import {
  getConsentMapping,
  isOptionalConsent,
  isMandatoryConsent,
} from '../../config/consent-mapping.config';
import type { Consent } from '../../models/consent.enum';

@Component({
  selector: 'app-optout-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CrntButton,
    CrntCallout,
    HeaderComponent,
    FooterComponent,
    EmailPreferenceCardComponent,
    MandatoryEmailInfoCardComponent,
  ],
  templateUrl: './optout-page.component.html',
  styleUrl: './optout-page.component.scss',
})
export class OptOutPageComponent implements OnInit {
  readonly #preferenceFormService = inject(PreferenceFormService);
  readonly #optOutService = inject(OptOutService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #themeService = inject(ThemeService);
  readonly #titleService = inject(Title);
  readonly #tokenService = inject(TokenService);
  readonly translationService = inject(TranslationService);

  // State signals
  readonly isSaving = signal(false);
  readonly saveSuccess = signal(false);
  readonly saveError = signal(false);
  readonly tokenError = signal<'expired' | 'invalid' | 'no-token' | null>(null);
  readonly decodedToken = signal<OptoutJwtTokenPayload | null>(null);
  readonly jwtToken = signal<string | null>(null);
  readonly savedOptedInConsents = signal<string[]>([]);
  readonly savedOptedOutConsents = signal<string[]>([]);

  // Computed values
  readonly tokenErrorMessage = computed(() => {
    const error = this.tokenError();
    if (!error) return null;
    const t = this.translationService.t();
    const messages: Record<string, string> = {
      expired: t.tokenExpired,
      invalid: t.tokenValidationFailed,
      'no-token': t.noTokenFound,
    };
    return messages[error];
  });

  readonly preferenceForm = computed(() => this.#preferenceFormService.form);

  readonly optionalEmailPreferenceCards = computed(() => {
    const token = this.decodedToken();
    if (!token) return [];

    const consentMapping = getConsentMapping(this.translationService);
    return token.consent
      .filter((consent) => isOptionalConsent(consent))
      .map((consent) => ({
        ...consentMapping[consent.toLowerCase() as Consent],
        controlName: consent,
      }));
  });

  readonly hasOptionalConsents = computed(() => {
    const token = this.decodedToken();
    return token?.consent.some((consent) => isOptionalConsent(consent)) ?? false;
  });

  readonly mandatoryEmailInfoCards = computed(() => {
    const t = this.translationService.t();
    return [
      {
        title: 'Info en servicemail',
        description:
          'Over je contract, facturen en termijnbedrag. Deze verplichte en/of belangrijke, relevante informatie sturen we standaard per e-mail. Wil je deze liever per post ontvangen?',
        buttonText: 'Naar klantenservice',
        buttonLink: t.customerServiceUrl,
      },
      {
        title: t.loyaltyProgram,
        description: `Je ontvangt informatie over je deelname aan ons klantprogramma, bespaartips, en verrassingen. Wil je deze e-mails niet ontvangen? Schrijf je dan uit voor ${t.loyaltyProgram}.`,
        buttonText: `Naar ${t.loyaltyProgram}`,
        buttonLink: t.loyaltyProgramUrl,
      },
    ];
  });

  private updatePageTitle(): void {
    this.#titleService.setTitle(this.translationService.t().pageTitle);
  }

  private updateFavicon(): void {
    this.#themeService.updateFavicon(this.translationService.t().faviconPath);
  }

  ngOnInit(): void {
    const token = this.#activatedRoute.snapshot.queryParams['token'];

    if (!token) {
      this.tokenError.set('no-token');
      this.updateFavicon();
      return;
    }

    this.jwtToken.set(token);
    this.processToken(token);
  }

  private processToken(token: string): void {
    const result = this.#tokenService.processToken(token);

    if (result.brand) {
      this.#themeService.setBrand(result.brand);
      this.updatePageTitle();
      this.updateFavicon();
    }

    if (!result.success) {
      this.tokenError.set(result.error === 'expired' ? 'expired' : 'invalid');
      return;
    }

    this.tokenError.set(null);
    this.decodedToken.set(result.payload);
    const optionalConsents = result.payload.consent.filter((consent) => isOptionalConsent(consent));
    this.#preferenceFormService.initializeFormControls(optionalConsents, true);
  }

  onCheckboxChange(controlName: string, checked: boolean): void {
    this.preferenceForm().get(controlName)?.setValue(checked);
  }

  savePreferences(): void {
    const form = this.preferenceForm();
    const token = this.jwtToken();
    const decoded = this.decodedToken();

    if (!form.valid || !token || !decoded) return;

    this.isSaving.set(true);
    this.saveSuccess.set(false);
    this.saveError.set(false);

    const optionalConsents = decoded.consent.filter((consent) => isOptionalConsent(consent));
    const mandatoryConsents = decoded.consent.filter((consent) => isMandatoryConsent(consent));

    const preferences = [
      ...optionalConsents.map((consent) => ({
        consent: consent as Consent,
        choice: form.get(consent)?.value ?? true,
      })),
      ...mandatoryConsents.map((consent) => ({
        consent: consent as Consent,
        choice: true,
      })),
    ];

    const consentMapping = getConsentMapping(this.translationService);
    const optedIn: string[] = [];
    const optedOut: string[] = [];
    for (const consent of optionalConsents) {
      const title = consentMapping[consent.toLowerCase() as Consent]?.title ?? consent;
      if (form.get(consent)?.value) {
        optedIn.push(title);
      } else {
        optedOut.push(title);
      }
    }

    this.#optOutService.savePreferences({ token, preferences }).subscribe({
      next: () => {
        this.savedOptedInConsents.set(optedIn);
        this.savedOptedOutConsents.set(optedOut);
        this.isSaving.set(false);
        this.saveSuccess.set(true);
        this.saveError.set(false);
      },
      error: () => {
        this.isSaving.set(false);
        this.saveSuccess.set(false);
        this.saveError.set(true);
      },
    });
  }

  navigateToPreferences(): void {
    window.location.href = this.translationService.t().emailPreferencesUrl;
  }
}
